// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  RunCommandDocumentBase,
  VirtualMachineRunCommandsListOptionalParams,
  VirtualMachineRunCommand,
  VirtualMachineRunCommandsListByVirtualMachineOptionalParams,
  VirtualMachineRunCommandsGetOptionalParams,
  VirtualMachineRunCommandsGetResponse,
  VirtualMachineRunCommandsGetByVirtualMachineOptionalParams,
  VirtualMachineRunCommandsGetByVirtualMachineResponse,
  VirtualMachineRunCommandsCreateOrUpdateOptionalParams,
  VirtualMachineRunCommandsCreateOrUpdateResponse,
  VirtualMachineRunCommandUpdate,
  VirtualMachineRunCommandsUpdateOptionalParams,
  VirtualMachineRunCommandsUpdateResponse,
  VirtualMachineRunCommandsDeleteOptionalParams,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a VirtualMachineRunCommands. */
export interface VirtualMachineRunCommands {
  /**
   * Lists all available run commands for a subscription in a location.
   * @param location The name of Azure region.
   * @param options The options parameters.
   */
  list(
    location: string,
    options?: VirtualMachineRunCommandsListOptionalParams,
  ): PagedAsyncIterableIterator<RunCommandDocumentBase>;
  /**
   * The operation to get all run commands of a Virtual Machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param options The options parameters.
   */
  listByVirtualMachine(
    resourceGroupName: string,
    vmName: string,
    options?: VirtualMachineRunCommandsListByVirtualMachineOptionalParams,
  ): PagedAsyncIterableIterator<VirtualMachineRunCommand>;
  /**
   * Gets specific run command for a subscription in a location.
   * @param location The name of Azure region.
   * @param commandId Specifies a commandId of predefined built-in script. Command IDs available for
   *                  Linux are listed at https://aka.ms/RunCommandManagedLinux#available-commands, Windows at
   *                  https://aka.ms/RunCommandManagedWindows#available-commands.
   * @param options The options parameters.
   */
  get(
    location: string,
    commandId: string,
    options?: VirtualMachineRunCommandsGetOptionalParams,
  ): Promise<VirtualMachineRunCommandsGetResponse>;
  /**
   * The operation to get the run command.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param runCommandName The name of the VirtualMachineRunCommand
   * @param options The options parameters.
   */
  getByVirtualMachine(
    resourceGroupName: string,
    vmName: string,
    runCommandName: string,
    options?: VirtualMachineRunCommandsGetByVirtualMachineOptionalParams,
  ): Promise<VirtualMachineRunCommandsGetByVirtualMachineResponse>;
  /**
   * The operation to create or update the run command.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param runCommandName The name of the VirtualMachineRunCommand
   * @param runCommand Parameters supplied to the Create Virtual Machine RunCommand operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    vmName: string,
    runCommandName: string,
    runCommand: VirtualMachineRunCommand,
    options?: VirtualMachineRunCommandsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualMachineRunCommandsCreateOrUpdateResponse>,
      VirtualMachineRunCommandsCreateOrUpdateResponse
    >
  >;
  /**
   * The operation to create or update the run command.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param runCommandName The name of the VirtualMachineRunCommand
   * @param runCommand Parameters supplied to the Create Virtual Machine RunCommand operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    vmName: string,
    runCommandName: string,
    runCommand: VirtualMachineRunCommand,
    options?: VirtualMachineRunCommandsCreateOrUpdateOptionalParams,
  ): Promise<VirtualMachineRunCommandsCreateOrUpdateResponse>;
  /**
   * The operation to update the run command.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param runCommandName The name of the VirtualMachineRunCommand
   * @param runCommand Parameters supplied to the Update Virtual Machine RunCommand operation.
   * @param options The options parameters.
   */
  beginUpdate(
    resourceGroupName: string,
    vmName: string,
    runCommandName: string,
    runCommand: VirtualMachineRunCommandUpdate,
    options?: VirtualMachineRunCommandsUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualMachineRunCommandsUpdateResponse>,
      VirtualMachineRunCommandsUpdateResponse
    >
  >;
  /**
   * The operation to update the run command.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param runCommandName The name of the VirtualMachineRunCommand
   * @param runCommand Parameters supplied to the Update Virtual Machine RunCommand operation.
   * @param options The options parameters.
   */
  beginUpdateAndWait(
    resourceGroupName: string,
    vmName: string,
    runCommandName: string,
    runCommand: VirtualMachineRunCommandUpdate,
    options?: VirtualMachineRunCommandsUpdateOptionalParams,
  ): Promise<VirtualMachineRunCommandsUpdateResponse>;
  /**
   * The operation to delete the run command.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param runCommandName The name of the VirtualMachineRunCommand
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    vmName: string,
    runCommandName: string,
    options?: VirtualMachineRunCommandsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * The operation to delete the run command.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param vmName The name of the VirtualMachine
   * @param runCommandName The name of the VirtualMachineRunCommand
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    vmName: string,
    runCommandName: string,
    options?: VirtualMachineRunCommandsDeleteOptionalParams,
  ): Promise<void>;
}
